'server only';
import StoryblokClient, { ISbConfig } from 'storyblok-js-client';
import * as qs from 'qs';

import { STORYBLOK_TOKENS, STORYBLOK_SPACE_ID } from '@monoimin/constants';

import Logger from '../debug/Logger';
import sleep from '../helpers/sleep';

const CONFIG = {
  RATE_LIMIT: {
    INITIAL_DELAY: 500,
    MAX_DELAY: 1200,
    MAX_RETRIES: 14,
    BACKOFF_FACTOR: 1.5,
  },
  CACHE: {
    REVALIDATE_TIME: 10800, // 3 hour
    CACHE_TAGS: ['content'] as string[],
  },
  TIMEOUT: 2500,
} as const;

const logger = new Logger('Storyblok API', 'server', true);

const getResponseHeaders = (response: Response): Record<string, string> => {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
};

const logResponse = (
  response: Response,
  url: string,
  startTime: number,
  type: 'INFO' | 'ERROR' = 'INFO',
  error?: any
) => {
  const headers = getResponseHeaders(response);
  const amzCfId = headers['x-amz-cf-id'] ?? 'unknown x-amz-cf-id';

  const xCache = headers['x-cache'] ?? 'unknown cache';

  logger.logApiRequest({
    type,
    endpoint: url,
    cacheStatus: xCache,
    time: Date.now() - startTime,
    headers,
    cfId: amzCfId,
    ...(error && { error }),
  });
};

const tryFetch = async (
  attempts: number,
  url: RequestInfo | URL,
  options?: RequestInit,
  cacheTime?: number
): Promise<Response> => {
  const startTime = Date.now();
  const urlString = url.toString();
  const shouldCache = cacheTime && cacheTime > 0;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CONFIG.TIMEOUT);
  try {
    const baseOptions: RequestInit = {
      ...options,
      signal: controller.signal,
    };

    let fetchOptions: NextFetchRequestConfig | RequestInit;
    if (shouldCache) {
      fetchOptions = {
        ...baseOptions,
        next: {
          revalidate: cacheTime,
          tags: [...CONFIG.CACHE.CACHE_TAGS],
        },
      };
    } else {
      fetchOptions = {
        ...baseOptions,
        cache: 'no-store',
      };
    }

    const res = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    if (res?.status === 429 || res?.status >= 500) {
      if (attempts >= CONFIG.RATE_LIMIT.MAX_RETRIES) {
        logResponse(res, urlString, startTime, 'ERROR');
        return res;
      }

      const delay = Math.min(
        CONFIG.RATE_LIMIT.INITIAL_DELAY *
          Math.pow(CONFIG.RATE_LIMIT.BACKOFF_FACTOR, attempts),
        CONFIG.RATE_LIMIT.MAX_DELAY
      );

      logger.lap(
        `Status 429, retrying after ${delay}ms (attempt ${attempts + 1}/${
          CONFIG.RATE_LIMIT.MAX_RETRIES
        })`
      );
      await sleep(delay);

      return tryFetch(attempts + 1, url, options, cacheTime);
    }

    logResponse(res, urlString, startTime);
    return res;
  } catch (error) {
    // Clear the timeout to prevent memory leaks
    clearTimeout(timeoutId);

    // Handle timeout error specifically
    if (error instanceof DOMException && error.name === 'AbortError') {
      logger.lap(`Request timed out after ${CONFIG.TIMEOUT}ms: ${urlString}`);
      const res = new Response(null, {
        status: 408,
        statusText: 'Request Timeout',
      });
      logResponse(res, urlString, startTime, 'ERROR', 'Request timeout');
      return res;
    }
    if (attempts >= CONFIG.RATE_LIMIT.MAX_RETRIES) {
      const res = new Response(null, { status: 500 });
      logResponse(res, urlString, startTime, 'ERROR', error);
      throw error;
    }

    const delay = CONFIG.RATE_LIMIT.INITIAL_DELAY;

    logger.lap(
      `Fetch error, retrying after ${delay}ms (attempt ${attempts + 1}/${
        CONFIG.RATE_LIMIT.MAX_RETRIES
      })`
    );
    await sleep(delay);

    return tryFetch(attempts + 1, url, options, cacheTime);
  }
};

export const newStoryblokApi = (
  isDraft: boolean,
  _?: 'server',
  cacheTime?: number
) => {
  const finalCacheTime = isDraft
    ? 0
    : typeof cacheTime === 'number'
    ? cacheTime
    : CONFIG.CACHE.REVALIDATE_TIME;

  const params: ISbConfig = {
    accessToken: STORYBLOK_TOKENS.server.preview,
    fetch: async function (url: RequestInfo | URL, options?: RequestInit) {
      const fetchFreshData = isDraft; // Don't cache in dev or draft mode
      const startTime = Date.now();

      if (fetchFreshData || url instanceof Request) {
        const response = await fetch(url, {
          ...options,
          cache: 'no-store',
        });
        logResponse(response, url.toString(), startTime);
        return response;
      }

      try {
        const urlObj = new URL(url.toString());
        const searchParams = urlObj.searchParams.toString();

        const parsedSearch = searchParams ? qs.parse(searchParams) : {};

        const sortedSearch = qs.stringify(parsedSearch, {
          sort: (a, b) => (b > a ? -1 : 1),
        });
        urlObj.search = sortedSearch;
        const finalUrl = urlObj.toString();

        const cacheOptions: NextFetchRequestConfig = {
          ...options,
          next: {
            revalidate: finalCacheTime,
            tags: [...CONFIG.CACHE.CACHE_TAGS],
          },
        };

        const response = await tryFetch(
          0,
          finalUrl,
          cacheOptions,
          finalCacheTime
        );
        logResponse(response, finalUrl, startTime, 'INFO');
        return response;
      } catch (error: any) {
        await sleep(1000);
        const fallbackResponse = await fetch(url, options).catch(() => {
          return new Response(
            JSON.stringify({ error: 'Failed to fetch data' }),
            {
              status: 500,
              headers: { 'Content-Type': 'application/json' },
            }
          );
        });

        logResponse(
          fallbackResponse,
          url.toString(),
          startTime,
          'ERROR',
          error
        );
        return fallbackResponse;
      }
    },
  };

  const Storyblok = new StoryblokClient(params);

  return {
    Storyblok,
    spaceId: STORYBLOK_SPACE_ID,
    revalidate: finalCacheTime,
  };
};
